import { useState, useEffect, useMemo, useCallback } from "react";

export interface ColumnConfig {
  key: string;
  title: string;
  order?: number;
  alwaysVisible?: boolean;
  defaultVisible?: boolean;
  group?: string;
}

interface TableSelectionConfig {
  [tableId: string]: string[];
}

interface UseColumnSelectorProps {
  tableId: string;
  availableColumns: ColumnConfig[];
}

export function useColumnSelector({
  tableId,
  availableColumns,
}: UseColumnSelectorProps) {
  const { alwaysVisibleColumns, defaultVisibleColumns, selectableColumns } =
    useMemo(() => {
      const alwaysVisible = availableColumns
        .filter((col) => col.alwaysVisible)
        .map((col) => col.key);

      const defaultVisible = availableColumns
        .filter((col) => col.defaultVisible && !col.alwaysVisible)
        .map((col) => col.key);

      const selectable = availableColumns
        .filter((col) => !col.alwaysVisible)
        .map((col) => col.key);

      return {
        alwaysVisibleColumns: alwaysVisible,
        defaultVisibleColumns: defaultVisible,
        selectableColumns: selectable,
      };
    }, [availableColumns]);

  const [visibleColumns, setVisibleColumns] = useState<string[]>([]);

  // Cargar configuración inicial
  useEffect(() => {
    const savedConfig = localStorage.getItem("tableSelectionConfig");

    let columnsToSet: string[];

    if (savedConfig) {
      try {
        const config: TableSelectionConfig = JSON.parse(savedConfig);
        const savedColumns = config[tableId];

        if (savedColumns && Array.isArray(savedColumns)) {
          // Filtrar columnas válidas
          const validColumns = savedColumns.filter((col) =>
            selectableColumns.includes(col)
          );

          columnsToSet = [...alwaysVisibleColumns, ...validColumns];
        } else {
          columnsToSet = [...alwaysVisibleColumns, ...defaultVisibleColumns];
        }
      } catch (error) {
        console.error("Error loading column selection:", error);
        columnsToSet = [...alwaysVisibleColumns, ...defaultVisibleColumns];
      }
    } else {
      columnsToSet = [...alwaysVisibleColumns, ...defaultVisibleColumns];
    }

    // Eliminar duplicados
    columnsToSet = Array.from(new Set(columnsToSet));
    setVisibleColumns(columnsToSet);
  }, [tableId, alwaysVisibleColumns, defaultVisibleColumns, selectableColumns]);

  // Guardar configuración cuando cambien las columnas visibles
  useEffect(() => {
    if (visibleColumns.length === 0) return;

    const savedConfig = localStorage.getItem("tableSelectionConfig");
    const config: TableSelectionConfig = savedConfig
      ? JSON.parse(savedConfig)
      : {};

    // Solo guardar las columnas seleccionables
    const columnsToSave = visibleColumns.filter(
      (col) => !alwaysVisibleColumns.includes(col)
    );

    config[tableId] = columnsToSave;
    localStorage.setItem("tableSelectionConfig", JSON.stringify(config));
  }, [visibleColumns, tableId, alwaysVisibleColumns]);

  const toggleColumn = useCallback(
    (columnKey: string) => {
      if (alwaysVisibleColumns.includes(columnKey)) return;

      setVisibleColumns((prev) => {
        if (prev.includes(columnKey)) {
          return prev.filter((col) => col !== columnKey);
        } else {
          return [...prev, columnKey];
        }
      });
    },
    [alwaysVisibleColumns]
  );

  const toggleAllColumns = useCallback(
    (selected: boolean) => {
      setVisibleColumns(
        selected
          ? [...alwaysVisibleColumns, ...selectableColumns]
          : [...alwaysVisibleColumns]
      );
    },
    [alwaysVisibleColumns, selectableColumns]
  );

  const { allSelected, indeterminate } = useMemo(() => {
    const selectedSelectable = visibleColumns.filter(
      (col) => !alwaysVisibleColumns.includes(col)
    );

    return {
      allSelected: selectedSelectable.length === selectableColumns.length,
      indeterminate:
        selectedSelectable.length > 0 &&
        selectedSelectable.length < selectableColumns.length,
    };
  }, [visibleColumns, alwaysVisibleColumns, selectableColumns]);

  const dropdownButtonText = useMemo(() => {
    const selectedCount = visibleColumns.filter(
      (col) => !alwaysVisibleColumns.includes(col)
    ).length;

    if (selectedCount === 0) return "Sin columnas seleccionadas";
    if (selectedCount === selectableColumns.length) return "Todas las columnas";
    return `${selectedCount} columna${
      selectedCount !== 1 ? "s" : ""
    } seleccionada${selectedCount !== 1 ? "s" : ""}`;
  }, [visibleColumns, alwaysVisibleColumns, selectableColumns]);

  return {
    visibleColumns,
    toggleColumn,
    toggleAllColumns,
    allSelected,
    indeterminate,
    dropdownButtonText,
    alwaysVisibleColumns,
    defaultVisibleColumns,
  };
}
